/**
 * N2ScorecardPlaybookPage — how to turn a fresh notch into a trade.
 *
 * Route: /n2/scorecard/playbook
 *
 * The scorecard proves the models work; the playbook shows what to do
 * when one fires. Structured as: match-trade-to-model → size by hit
 * rate → combine models (confluence) → when to skip → exit at horizon.
 *
 * Examples use real cells from the live scorecard so a reader can
 * cross-reference every claim against the numbers on /n2/scorecard.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { N2Frame } from './N2Frame';
import { palette } from './theme';
import { Eyebrow, PageTitle, PageLede, SectionLabel } from './N2Primitives';
import { ScorecardSubNav } from './notchesScorecardShared';

export const N2ScorecardPlaybookPage: React.FC = () => (
  <N2Frame page="scorecard/playbook">
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 32px 40px' }}>
      <ScorecardSubNav />

      <div style={{ paddingTop: 24 }}>
        <Eyebrow>Scorecard · Playbook</Eyebrow>
        <PageTitle italic="what to do with a fresh one.">
          How to trade a notch,
        </PageTitle>
        <PageLede>
          The scorecard shows every reading and the outcome that followed. The
          playbook shows how to turn a fresh reading into a trade — which
          expiration to use, how much size, and when two or more models
          agreeing changes the answer.
        </PageLede>
      </div>

      <MatchTradeSection />
      <SizingSection />
      <ConfluenceSection />
      <WhenToSkipSection />
      <ExitHorizonSection />

      <div style={{
        marginTop: 48, padding: '18px 24px',
        borderTop: `2px solid ${palette.line}`,
        fontFamily: 'var(--sans)', fontSize: 12,
        color: palette.ink3, lineHeight: 1.6,
      }}>
        The playbook is educational. Every example is a real notch cell from{' '}
        <Link to="/n2/scorecard" style={{ color: palette.goldDeep }}>the live scorecard</Link>,
        but nothing here is a personalized recommendation. Position sizing and
        risk management remain your responsibility.
      </div>
    </div>
  </N2Frame>
);

// ==== Match trade to model ================================================

const MATCH_ROWS: {
  model: string; fires: string; trade: string; dte: string;
}[] = [
  { model: 'COT', fires: 'Stretched LONG', trade: 'Put credit spread or long calls on the ETF proxy', dte: '30–45' },
  { model: 'COT', fires: 'Stretched SHORT', trade: 'Put debit spread or long puts', dte: '30–45' },
  { model: 'GEX', fires: 'NEG_GEX (short gamma)', trade: 'Long straddle / strangle; avoid iron condors', dte: '5–15' },
  { model: 'GEX', fires: 'POS_GEX (long gamma)', trade: 'Iron condor or credit spread; avoid straddles', dte: '5–15' },
  { model: 'IV',  fires: 'IVR crossed HIGH (≥70)', trade: 'Sell premium: put spreads, iron condors, covered calls', dte: '20–45' },
  { model: 'IV',  fires: 'IVR crossed LOW (≤30)', trade: 'Buy premium: long calls/puts, long straddles', dte: '30–60' },
  { model: 'AVWAP', fires: 'Support HELD (day 5 above)', trade: 'Bull put spread or long calls', dte: '20–45' },
  { model: 'AVWAP', fires: 'Support BROKE (day 5 below −2%)', trade: 'Bear call spread or long puts', dte: '15–30' },
];

const MatchTradeSection: React.FC = () => (
  <section style={{ marginTop: 48 }}>
    <SectionLabel left="1 · Match the trade to the model" right="" />
    <p style={{
      fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink3,
      margin: '10px 0 20px', lineHeight: 1.55,
    }}>
      Each model tests a different question at its own horizon. Trade at that
      horizon — the hit rate on the scorecard is measured against that specific window.
    </p>
    <div style={{ overflowX: 'auto' }}>
      <table style={{
        width: '100%', borderCollapse: 'collapse',
        fontFamily: 'var(--sans)', fontSize: 13,
      }}>
        <thead>
          <tr style={{ borderBottom: `1.5px solid ${palette.line}` }}>
            <Th w={80}>Model</Th>
            <Th w={220}>When it fires</Th>
            <Th>What to trade</Th>
            <Th w={80}>DTE</Th>
          </tr>
        </thead>
        <tbody>
          {MATCH_ROWS.map((r, i) => (
            <tr key={i} style={{ borderBottom: `1px dashed ${palette.line}` }}>
              <Td><ModelChip>{r.model}</ModelChip></Td>
              <Td style={{ color: palette.ink2 }}>{r.fires}</Td>
              <Td style={{ color: palette.ink }}>{r.trade}</Td>
              <Td style={{ color: palette.ink3, fontFamily: 'var(--mono)' }}>{r.dte}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

// ==== Sizing by hit rate ==================================================

interface SizingExample {
  tier: 'Full size' | 'Half size' | 'Skip' | 'Take normal, not oversize';
  tierAccent: 'positive' | 'caution' | 'negative' | 'gold';
  cell: string;
  hitPct: string;
  n: string;
  scenario: string;
  trade: string;
  rationale: string;
}

const SIZING_EXAMPLES: SizingExample[] = [
  {
    tier: 'Full size',
    tierAccent: 'positive',
    cell: 'GC COT',
    hitPct: '68%',
    n: '44',
    scenario:
      'GC COT fires LM_STRETCHED_LONG. Leveraged funds are extremely positioned long in gold futures.',
    trade:
      'Bull put spread on GLD, 30–45 DTE. Sell ~30-delta put, buy ~15-delta put one width lower. Full-size position (~1× your normal per-trade risk).',
    rationale:
      '68% hit rate over 44 resolved readings is the strongest cell on the board. The model has publicly called this direction correctly two out of three times, over three years of live data. Trust it at full size.',
  },
  {
    tier: 'Half size',
    tierAccent: 'caution',
    cell: 'IV XLV',
    hitPct: '48%',
    n: '29',
    scenario:
      'XLV IVR crosses INTO high (≥70). Health-care options premium is expensive relative to XLV\'s own history.',
    trade:
      'Iron condor on XLV, 20–45 DTE. Wings 1× width. Take HALF your normal position size.',
    rationale:
      '48% hit rate over 29 readings is roughly coin-flip — the mean-reversion still happens often enough to be a small edge, but not enough to size up. Half-size respects the marginal reading.',
  },
  {
    tier: 'Skip',
    tierAccent: 'negative',
    cell: 'ES COT · IWM GEX · XLK IV',
    hitPct: '37% / 25% / 32%',
    n: '30 / 12 / 22',
    scenario:
      'ES COT fires stretched-short, or IWM GEX regime changes, or XLK IVR crosses into high.',
    trade:
      'Skip — or take the OTHER side.',
    rationale:
      'These cells have failed publicly for three years. Trading with the model on these markets is worse than a coin flip. If you want to trade, consider fading (taking the opposite side): 63% success on ES COT SHORT means selling naked puts on SPY when the model calls it, sized appropriately.',
  },
  {
    tier: 'Take normal, not oversize',
    tierAccent: 'gold',
    cell: 'SPY AVWAP',
    hitPct: '100%',
    n: '7',
    scenario:
      'SPY tests its 60-day swing-low AVWAP. Historically every one of these tests has held over the next 5 days.',
    trade:
      'Bull put spread or long calls on SPY, 20–45 DTE. Full NORMAL size (~1×). Not oversize.',
    rationale:
      '100% at n=7 sounds like automatic money — it isn\'t. Small samples flatter. The trigger is strict (spot has to be 3%+ above AVWAP for 10 days, then dip within 0.5%), which is genuinely rare, and past success doesn\'t bind future outcomes. Treat as high-conviction but respect the small sample by sizing normally.',
  },
];

const SizingSection: React.FC = () => (
  <section style={{ marginTop: 56 }}>
    <SectionLabel left="2 · Size to the hit rate" right="" />
    <p style={{
      fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink3,
      margin: '10px 0 20px', lineHeight: 1.55,
    }}>
      The scorecard publishes the losers alongside the winners so you can size accordingly.
      Same trade, different symbol — different sizing. Below are four worked examples
      using real cells from tonight's scorecard.
    </p>
    <div style={{
      display: 'grid', gap: 16,
      gridTemplateColumns: 'repeat(auto-fit, minmax(430px, 1fr))',
    }}>
      {SIZING_EXAMPLES.map((ex, i) => <SizingCard key={i} ex={ex} />)}
    </div>
  </section>
);

const SizingCard: React.FC<{ ex: SizingExample }> = ({ ex }) => {
  const accent = {
    positive: palette.positive,
    caution: palette.caution,
    negative: palette.negative,
    gold: palette.goldDeep,
  }[ex.tierAccent];
  return (
    <div style={{
      border: `1px solid ${palette.line}`, background: palette.paper,
      padding: '20px 22px 18px', borderRadius: 3,
      display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      }}>
        <span style={{
          fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          padding: '4px 10px', borderRadius: 2,
          background: `${accent}18`, color: accent,
          border: `1px solid ${accent}55`,
        }}>{ex.tier}</span>
        <span style={{
          fontFamily: 'var(--serif)', fontSize: 16, color: palette.ink, marginLeft: 4,
        }}>{ex.cell}</span>
        <span style={{
          marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 12, color: palette.ink3,
        }}>
          {ex.hitPct} · n = {ex.n}
        </span>
      </div>
      <LabeledLine label="Scenario" body={ex.scenario} />
      <LabeledLine label="Trade" body={ex.trade} bold />
      <LabeledLine label="Why" body={ex.rationale} muted />
    </div>
  );
};

// ==== Combine models (confluence) =========================================

interface ConfluenceExample {
  kind: 'Aligned bullish' | 'Aligned bearish + long-vol' | 'Contradicting';
  kindAccent: 'positive' | 'negative' | 'caution';
  market: string;
  members: string[];       // "COT LONG on ES", "IV HIGH on SPY", ...
  interpretation: string;
  trade: string;
  size: string;
}

const CONFLUENCE_EXAMPLES: ConfluenceExample[] = [
  {
    kind: 'Aligned bullish',
    kindAccent: 'positive',
    market: 'S&P 500',
    members: [
      'COT · ES · Stretched LONG',
      'IV · SPY · Crossed HIGH (IVR 72)',
      'GEX · SPY · POS_GEX_ABOVE_FLIP',
    ],
    interpretation:
      'Institutional positioning is bullish (COT), premium is expensive (IV), and the gamma regime says grinding tight range (GEX). Three models agree the setup favors premium sellers — direction UP + vol DOWN.',
    trade:
      'Put credit spread on SPY, 20–45 DTE, 25–30 delta short strike. Optionally add a call credit spread wider out (iron condor) to double up on the vol-compression call.',
    size:
      '1.5× your normal size. Aligned confluence is the highest-conviction setup on the board — worth sizing up, not maxing out.',
  },
  {
    kind: 'Aligned bearish + long-vol',
    kindAccent: 'negative',
    market: 'Nasdaq-100',
    members: [
      'COT · NQ · Stretched SHORT',
      'GEX · QQQ · NEG_GEX_BELOW_FLIP',
      'AVWAP · QQQ · Support BROKE (day 5 close −3%)',
    ],
    interpretation:
      'Three models point down at once: institutions positioned short (COT), dealers short gamma so vol will amplify (GEX), and the institutional support level just failed (AVWAP). Both the direction and the vol call are bearish.',
    trade:
      'Long puts on QQQ, 15–30 DTE, ~40-delta. A single instrument that captures both the direction call (delta) and the vol call (long theta+vega). Alternative: put debit spread if premium is too rich.',
    size:
      '1.5× normal. Two dimensions confirming the same trade is a rare high-conviction moment; don\'t overweight, don\'t under-participate.',
  },
  {
    kind: 'Contradicting',
    kindAccent: 'caution',
    market: 'Russell 2000',
    members: [
      'COT · IWM · Stretched LONG (bullish)',
      'IV · IWM · Crossed LOW (IVR 22) — implies vol expansion coming',
      'AVWAP · IWM · Support BROKE (bearish)',
    ],
    interpretation:
      'COT says bullish, AVWAP says the level just failed (bearish), and IV low says vol is about to expand in EITHER direction. Direction dimension is split. This is a "wait" reading, not a trade setup.',
    trade:
      'Half-size or wait for resolution. If you must be in the market: a long straddle (vol expansion is the one thing all three agree could happen) sized SMALL because IWM IV cell hit rate is only 40%.',
    size:
      '0× to 0.5×. When directional models contradict, sitting on your hands is a position. Come back when one side resolves.',
  },
];

const ConfluenceSection: React.FC = () => (
  <section style={{ marginTop: 56 }}>
    <SectionLabel left="3 · Combine across models" right="" />
    <p style={{
      fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink3,
      margin: '10px 0 20px', lineHeight: 1.55,
    }}>
      Any single notch is a data point. Two or more independent models firing on the
      same market within a week is a regime read — the Confluence band on the
      overview surfaces these automatically. This is the highest-conviction cell
      on the site; also the one that separates readers from users.
    </p>
    <div style={{
      display: 'grid', gap: 16,
      gridTemplateColumns: 'repeat(auto-fit, minmax(430px, 1fr))',
    }}>
      {CONFLUENCE_EXAMPLES.map((ex, i) => <ConfluenceCard key={i} ex={ex} />)}
    </div>
  </section>
);

const ConfluenceCard: React.FC<{ ex: ConfluenceExample }> = ({ ex }) => {
  const accent = {
    positive: palette.positive,
    negative: palette.negative,
    caution: palette.caution,
  }[ex.kindAccent];
  return (
    <div style={{
      border: `1px solid ${palette.line}`, background: palette.paper,
      padding: '20px 22px 18px', borderRadius: 3,
      display: 'flex', flexDirection: 'column', gap: 12,
      boxShadow: `inset 3px 0 0 ${accent}`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      }}>
        <span style={{
          fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          padding: '4px 10px', borderRadius: 2,
          background: `${accent}18`, color: accent,
          border: `1px solid ${accent}55`,
        }}>{ex.kind}</span>
        <span style={{
          fontFamily: 'var(--serif)', fontSize: 18, color: palette.ink, marginLeft: 4,
        }}>{ex.market}</span>
      </div>
      <div>
        <div style={{
          fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          color: palette.ink3, marginBottom: 6,
        }}>Notches firing</div>
        <ul style={{
          margin: 0, paddingLeft: 18,
          fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink2, lineHeight: 1.5,
        }}>
          {ex.members.map((m, i) => <li key={i}>{m}</li>)}
        </ul>
      </div>
      <LabeledLine label="Interpretation" body={ex.interpretation} />
      <LabeledLine label="Trade" body={ex.trade} bold />
      <LabeledLine label="Size" body={ex.size} muted />
    </div>
  );
};

// ==== When to skip + exit ================================================

const WhenToSkipSection: React.FC = () => (
  <section style={{ marginTop: 56 }}>
    <SectionLabel left="4 · When NOT to trade" right="" />
    <ul style={{
      marginTop: 20, paddingLeft: 20,
      fontFamily: 'var(--sans)', fontSize: 14, lineHeight: 1.7, color: palette.ink,
    }}>
      <li>
        <b>No live notches + no recent confluence.</b> Not every day has a
        setup — don't invent one. The models fire when they fire. Days
        without live readings are days to hold cash and read.
      </li>
      <li>
        <b>Only reading is on a below-coin-flip cell.</b> ES COT alone, IWM GEX alone,
        XLK IV alone — the model has publicly failed on these for three years.
        Don't trade an individual notch on a losing cell.
      </li>
      <li>
        <b>Multiple contradicting notches on the same market.</b> Split direction
        readings mean the models disagree. Wait for one to resolve rather than
        picking a side.
      </li>
      <li>
        <b>Notch is past its outcome window.</b> A COT reading from 5 weeks ago is
        no longer a fresh reading — its 4-week horizon has resolved. Look at
        this week's notches, not the log.
      </li>
    </ul>
  </section>
);

const ExitHorizonSection: React.FC = () => (
  <section style={{
    marginTop: 56, padding: '24px 28px',
    border: `1px solid ${palette.line}`, background: palette.paper2, borderRadius: 3,
  }}>
    <SectionLabel left="5 · Exit at the model's own horizon" right="" />
    <p style={{
      fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink2,
      margin: '10px 0 12px', lineHeight: 1.6,
    }}>
      Hit rates on the scorecard are measured against the model's specific window.
      Holding a trade past that window is trading on a hunch, not on the model.
    </p>
    <ul style={{
      margin: 0, paddingLeft: 20,
      fontFamily: 'var(--sans)', fontSize: 13, lineHeight: 1.7, color: palette.ink,
    }}>
      <li><b>COT</b> — hold up to 4 weeks (28 days) from the reading date.</li>
      <li><b>GEX</b> — hold up to 5 trading days after the regime change.</li>
      <li><b>IV</b> — hold up to 10 trading days, or close early when IVR crosses back past 50.</li>
      <li><b>AVWAP</b> — hold up to 5 trading days after the test event.</li>
    </ul>
  </section>
);

// ==== small primitives ===================================================

const LabeledLine: React.FC<{
  label: string; body: string; bold?: boolean; muted?: boolean;
}> = ({ label, body, bold, muted }) => (
  <div>
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
      letterSpacing: '0.14em', textTransform: 'uppercase',
      color: palette.ink3, marginBottom: 4,
    }}>{label}</div>
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 13,
      color: muted ? palette.ink3 : palette.ink2,
      fontStyle: muted ? 'italic' : 'normal',
      fontWeight: bold ? 500 : 400,
      lineHeight: 1.55,
    }}>{body}</div>
  </div>
);

const ModelChip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: 2,
    fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
    letterSpacing: '0.14em', textTransform: 'uppercase',
    border: `1px solid ${palette.line}`, color: palette.ink2,
  }}>{children}</span>
);

const Th: React.FC<{ children: React.ReactNode; w?: number }> = ({ children, w }) => (
  <th style={{
    textAlign: 'left', padding: '12px 12px 12px 0',
    fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
    letterSpacing: '0.14em', textTransform: 'uppercase', color: palette.ink3,
    width: w, whiteSpace: 'nowrap',
  }}>{children}</th>
);

const Td: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <td style={{
    padding: '14px 12px 14px 0', color: palette.ink, verticalAlign: 'top',
    ...style,
  }}>{children}</td>
);

export default N2ScorecardPlaybookPage;
